import { FC, useEffect } from "react";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker as DefaultDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import "moment/locale/fr";
import { useTranslation } from "../../hooks/useTranslation";
import { ISO_CODE_EN, ISO_CODE_FR } from "../../utils/translations";

interface Props {
  className?: string;
  label: string;
  showTime: boolean;
  currentValue: string | null;
  onChange: (value: string | null) => void;
}

const DateTimePicker: FC<Props> = ({
  className,
  label,
  showTime,
  currentValue,
  onChange,
}) => {
  const { language, t } = useTranslation();
  
  // Set moment locale based on language from hook
  useEffect(() => {
    if (language === ISO_CODE_FR) {
      moment.locale(ISO_CODE_FR);
    } else {
      moment.locale(ISO_CODE_EN);
    }
  }, [language]);

  const placeholderFormat = t("datePlaceholder");
  
  const datePickerProps = {
    label: "",
    components: {
      OpenPickerIcon: CalendarTodayIcon,
    },
    renderInput: (params: any) => (
      <TextField
        {...params}
        variant="standard"
        placeholder={placeholderFormat}
        inputProps={{
          ...params.inputProps,
          placeholder: placeholderFormat,
        }}
        sx={{
          "& .MuiInput-root": {
            paddingRight: "1rem",
          },
          "& .MuiInput-input": { textAlign: "center" },
        }}
      />
    ),
  };

  return (
    <Box
      className={className}
      sx={{
        display: { xs: "block", md: "flex" },
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          textTransform: "uppercase",
          color: "text.secondary",
          paddingX: "1rem",
        }}
      >
        {label}
      </Box>
      {showTime ? (
        <DefaultDateTimePicker
          inputFormat="yyyy-MM-dd h:mm:ss"
          value={currentValue}
          onChange={onChange}
          {...datePickerProps}
        />
      ) : (
        <DatePicker
          inputFormat="yyyy-MM-dd"
          value={currentValue}
          onChange={onChange}
          {...datePickerProps}
        />
      )}
    </Box>
  );
};

export default DateTimePicker;
